import { courseHeroBackground, bannerReferenceColor, isLightColor } from '../../../../utils/courseTheme';

const STYLE_PRESETS = [
  {
    key: 'default',
    label: 'Default',
    description: 'Solid dark banner, same as the rest of the site',
  },
  {
    key: 'gradient',
    label: 'Gradient',
    description: 'Diagonal gradient from your background color into your accent color',
  },
  {
    key: 'minimal',
    label: 'Minimal',
    description: 'Light, clean banner -- your accent color does the talking',
  },
  {
    key: 'bold',
    label: 'Bold',
    description: 'Full-bleed accent color banner for a punchy, branded look',
  },
];

function ColorField({ label, value, onChange, hint }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600 block mb-1.5">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer bg-white p-0.5"
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#0d9488"
          className="flex-1 px-3 py-2.5 border border-slate-200 rounded-lg text-sm font-mono text-slate-700 focus:outline-none focus:border-teal-400 bg-white"
        />
      </div>
      {hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

export default function ThemeEditor({ course, theme, onChange }) {
  const update = (patch) => onChange({ ...theme, ...patch });
  const heroBg = courseHeroBackground(theme);
  const onLight = isLightColor(bannerReferenceColor(theme));
  const textColor = onLight ? '#0f172a' : '#ffffff';
  const subTextColor = onLight ? 'rgba(15,23,42,0.65)' : 'rgba(255,255,255,0.75)';

  return (
    <div className="max-w-4xl space-y-6">
      <div className="bg-teal-50 border border-teal-100 rounded-xl p-4 flex items-start gap-2.5">
        <div className="w-5 h-5 flex items-center justify-center text-teal-500 flex-shrink-0 mt-0.5">
          <i className="ri-palette-line" />
        </div>
        <div>
          <p className="text-xs font-semibold text-teal-800">Make this course feel like yours</p>
          <p className="text-xs text-teal-700 mt-0.5">
            Pick a banner style and colors for this course&apos;s public page. Every course you teach can look different --
            students will see this on the course detail page and the enroll button.
          </p>
        </div>
      </div>

      {/* Live preview */}
      <div className="bg-white rounded-xl border border-slate-100 p-6">
        <h2 className="text-sm font-bold text-slate-700 pb-1 border-b border-slate-100 mb-4">Live Preview</h2>
        <div
          className="rounded-2xl p-6 md:p-8 transition-all"
          style={{ background: heroBg }}
        >
          <div className="flex flex-wrap gap-2 mb-3">
            <span
              className="text-[10px] font-bold px-2.5 py-1 rounded-full"
              style={{ background: onLight ? 'rgba(15,23,42,0.08)' : 'rgba(255,255,255,0.15)', color: textColor }}
            >
              {course.level || 'Beginner'}
            </span>
            <span
              className="text-[10px] font-bold px-2.5 py-1 rounded-full"
              style={{ background: theme.primaryColor, color: isLightColor(theme.primaryColor) ? '#0f172a' : '#ffffff' }}
            >
              {course.category || 'General'}
            </span>
          </div>
          <h3 className="text-xl font-bold leading-tight mb-1.5" style={{ color: textColor }}>
            {course.title || 'Your Course Title'}
          </h3>
          <p className="text-sm mb-4 max-w-md" style={{ color: subTextColor }}>
            {course.description || 'A short description of your course appears here, just like students will see it.'}
          </p>
          <button
            type="button"
            className="px-5 py-2.5 rounded-xl text-sm font-bold shadow-sm cursor-default"
            style={{ background: theme.primaryColor, color: isLightColor(theme.primaryColor) ? '#0f172a' : '#ffffff' }}
          >
            Enroll Now
          </button>
        </div>
      </div>

      {/* Style presets */}
      <div className="bg-white rounded-xl border border-slate-100 p-6">
        <h2 className="text-sm font-bold text-slate-700 pb-1 border-b border-slate-100 mb-4">Banner Style</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {STYLE_PRESETS.map((preset) => (
            <button
              key={preset.key}
              type="button"
              onClick={() => update({ style: preset.key })}
              className={`text-left p-3.5 rounded-xl border-2 transition-all cursor-pointer flex items-center gap-3 ${
                theme.style === preset.key
                  ? 'border-teal-600 bg-teal-50/50'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <div
                className="w-12 h-9 rounded-lg flex-shrink-0 border border-black/5"
                style={{ background: courseHeroBackground({ ...theme, style: preset.key }) }}
              />
              <div className="min-w-0">
                <p className="text-xs font-bold text-slate-800">{preset.label}</p>
                <p className="text-[11px] text-slate-500 leading-snug">{preset.description}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Colors */}
      <div className="bg-white rounded-xl border border-slate-100 p-6">
        <h2 className="text-sm font-bold text-slate-700 pb-1 border-b border-slate-100 mb-4">Colors</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ColorField
            label="Accent Color"
            value={theme.primaryColor}
            onChange={(v) => update({ primaryColor: v })}
            hint="Used for the enroll button, badges and highlights"
          />
          <ColorField
            label="Background Color"
            value={theme.backgroundColor}
            onChange={(v) => update({ backgroundColor: v })}
            hint="Used for the banner (ignored by Minimal and Bold styles)"
          />
        </div>
      </div>

      <button
        type="button"
        onClick={() => onChange({ style: 'default', primaryColor: '#0d9488', backgroundColor: '#0f172a' })}
        className="text-xs font-semibold text-slate-500 hover:text-rose-600 cursor-pointer inline-flex items-center gap-1.5"
      >
        <i className="ri-restart-line" /> Reset to default LearnFlow theme
      </button>
    </div>
  );
}
